import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { CreateTanResponseV2 } from '@/services/mobi-api/numberv2/types'
import { checkEmail } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import { Loop } from '@mui/icons-material'
import { Dialog, Divider, TextField } from '@mui/material'
import React, { FunctionComponent, useReducer, useState } from 'react'
import OTPInput from 'react-otp-input'
import { Obj } from '..'
import MCButton from '@/components/ui/Buttons/MobicomButton'
import { resolveImageUrl } from '@/utils/constants'
import Image from 'next/image'
import { TouristTanConfirmReq } from '@/services/mobi-api/tourist/types'
import router from 'next/router'

interface Props {
  next: (obj: Obj) => void
  back: () => void
  objs: Obj | undefined
}

interface State {
  loading?: boolean
  buttonLoading?: boolean
  otp?: string
  email?: string
  tanLocked?: boolean
  tanIdentifierLocked?: boolean
  dialogOpen?: boolean
  errorMessageMn?: string
  errorMessageEn?: string
  timerId?: NodeJS.Timer
  isTanCreated?: boolean
  tanRes?: CreateTanResponseV2
  counter?: number
}

const EmailInput: FunctionComponent<Props> = ({ next, back, objs }) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { locale } = useLanguage()
  const { tourist } = useMobiApi()
  const [counter, setCounter] = useState(0)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [state, setState] = useReducer(

    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      loading: false,
      buttonLoading: false,
      otp: '',
      email: '',
      tanLocked: false,
      tanIdentifierLocked: false,
      dialogOpen: false,
      isTanCreated: false,
      counter: 0
    }
  )

  const handleTanCreate = async (): Promise<void> => {
    try {
      clearTimer()
      setState({
        loading: true,
        errorMessageEn: undefined,
        errorMessageMn: undefined
      })
      const res = await tourist.touristCreateTanEmail({ email: state.email ?? '' })

      if (res?.error !== undefined && res?.errorCode !== null && res?.errorCode !== undefined) {
        showError(res?.error !== null && res?.error !== undefined ? res?.error : translate('tourist.request.failed'))
      }
      if (res?.response !== null && res?.response !== undefined && res.responseCode === 'SUCCESS') {
        setState({ tanRes: res?.response, email: state.email })
        setCounter(res.response.timeLeftInSec)
        startTimer()
      }
      if (res?.responseCode === 'TAN_CREATION_LOCKED') {
        setState({ tanRes: res?.response, tanLocked: true })
      }
      if (res?.responseCode === 'TAN_IDENTIFIER_LOCKED') {
        setState({ tanRes: res?.response, tanIdentifierLocked: true })
      }
    } catch (e) {
      showError(translate('tourist.request.failed'))
    } finally {
      setState({ isTanCreated: true, loading: false, otp: '' })
    }
  }

  const handleTanConfirm = async (): Promise<void> => {
    setState({ loading: true })
    try {
      const req: TouristTanConfirmReq = {
        email: state.email ?? '',
        code: state.otp ?? '',
        simType: objs?.choosenSim ?? '',
        selfsimcode: objs?.selfsimcode ?? undefined,
        imsi: objs?.imsi ?? undefined,
        imsiQr: objs?.imsiQr ?? undefined,
        packageCode: objs?.choosenPackage?.id ?? '',
        channel: 'mobicom_web',
        ratePlan: objs?.choosenPackage?.ratePlan ?? ''

      }
      const res = await tourist.touristConfirmTanEmail(req)
      if (res !== undefined) {
        if (res.responseCode === 'SUCCESS') {
          if (res.responseDescription !== undefined) {
            if (res.responseDescription.toUpperCase().includes('FINISHED')) {
              setIsModalOpen(true)
            } else {
              next({ email: state.email, instanceId: res.instanceId })
            }
          }
        }
        if (res.responseCode === 'TAN_MISMATCHED') {
          showError(translate('tourist.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_NOT_FOUND') {
          showError(translate('tourist.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_LOCKED') {
          setState({ otp: '' })
          clearTimer()
          showError(translate('tourist.tan_locked'))
        }
        if (res.responseCode !== 'SUCCESS') {
          if (res?.error !== undefined || res?.responseDescription !== undefined) {
            showError(res?.error !== null && res?.error !== undefined ? res?.error : res?.responseDescription !== null && res?.responseDescription !== undefined ? res?.responseDescription : translate('tourist.request.failed'))
          }
        }
      } else {
        showError(translate('tourist.request.failed'))
      }
    } catch (e) {
      showError(translate('tourist.request.failed'))
    } finally {
      setState({ loading: false })
    }
  }
  const onClose = async (): Promise<void> => {
    setIsModalOpen(false)
    await router.push('/')
  }
  const startTimer = (): void => {
    const intervalId = setInterval(() => {
      setCounter((prev) => prev - 1)
    }, 1000)
    setState({ timerId: intervalId })
  }
  const clearTimer = (): void => {
    clearInterval(state.timerId)
    setState({ timerId: undefined })
    setCounter(0)
  }
  const renderTimer = (): string => {
    if (counter !== undefined && counter > 0) {
      const min = Math.floor(counter / 60)
      const sec = Math.floor(counter % 60)
      const minStr = min < 10 ? '0' + min.toString() : min.toString()
      const secStr = sec < 10 ? '0' + sec.toString() : sec.toString()

      return minStr + ':' + secStr
    } else {
      return '00:00'
    }
  }
  const renderDuration = (time: number | undefined): string => {
    if (time === undefined || time === null) {
      return '?'
    }
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time - (hours * 3600)) / 60)
    const minutesStr: string = minutes < 10 ? '0' + minutes.toString() : minutes.toString()

    if (locale === 'en') {
      return hours > 0 ? hours.toString() + ' hours and ' + minutesStr + ' minutes' : minutesStr + ' minutes'
    } else {
      return hours > 0 ? hours.toString() + ' цаг ' + minutesStr + ' минутын ' : minutesStr + ' минутын '
    }
  }
  if (state.loading === true) {
    return <div className='w-full h-96 flex items-center justify-center'><Loader /></div>
  }
  if (state.tanLocked === true) {
    return (
      <div className='p-4 py-24 flex-1 flex flex-col items-center gap-4'>
        <p className='text-center w-2/3 text-textMid'>
          {translate('tourist.tan_creation_locked').replace('$', renderDuration(state.tanRes?.creationLimit.lockTimeLeftSec))}
        </p>
      </div>
    )
  }
  if (state.tanIdentifierLocked === true) {
    return <div className='p-4 py-24 flex-1 flex flex-col items-center gap-4 text-textMid'>{translate('tourist.tan_creation_locked').replace('$', renderDuration(state.tanRes?.timeLeftInSec))}</div>
  }

  return (
    <div className='flex flex-col items-center justify-center gap-4 p-6 transition-all duration-700 bg-surfaceMid rounded-xl md:p-8 border-secondaryMidStroke'>
      {/* <div className='bg-surfaceMid rounded-xl p-6 md:p-8 flex flex-col items-center justify-center gap-4 transition-all duration-700 border-secondaryMidStroke'> */}
      <TextField
        label={translate('tourist.email')} variant='outlined' className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid' value={state.email} onChange={(e) => setState({ email: e.target.value })}
        InputLabelProps={{
          style: { color: 'var(--color-text-textHigh)' }
        }}
        InputProps={{
          style: {
            color: 'var(--color-text-textHigh)',
            '&::placeholder': {
              color: 'var(--color-text-secondaryHighStroke)'
            }
          } as any
        }}
        sx={{
          '& fieldset': {
            borderRadius: '8px'
          }
        }}
      />
      {
        state.isTanCreated !== null && state.isTanCreated === true
          ? (
            <div className='flex flex-col items-center justify-center gap-4 w-full'>
              <p className='text-textMid font-medium text-center md:w-4/5'>{translate('tourist.tan_sent').replace('$email', state.email ?? '')}</p>
              <div className='w-full flex justify-center h-fit'>
                <OTPInput
                  value={state.otp}
                  onChange={(otp) => setState({ otp })}
                  numInputs={6}
                  renderSeparator={<Divider orientation='vertical' />}
                  renderInput={(props: any) => <input {...props} />}
                  inputType='number'
                  inputStyle={{
                    width: '45px',
                    height: '45px',
                    fontSize: '18px',
                    color: 'textHigh',
                    fontWeight: '700',
                    caretColor: 'blue',
                    borderRadius: '8px',
                    backgroundColor: 'surfaceInverse'
                  }}
                  containerStyle={{
                    border: '1px solid #CFD3DB',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'surfaceInverse'
                  }}
                />
              </div>
              {
                state.tanRes !== undefined && (
                  <span className='text-textMid text-sm'>{renderTimer()}</span>
                )
              }
              <DarkButton type='outlined' onClick={() => { void handleTanCreate() }} leadingIcon={<Loop />}>
                {translate('tourist.tan.getagain')}
              </DarkButton>
              <DarkButton onClick={() => { void handleTanConfirm() }} fullwidth>
                {translate('tourist.continue')}
              </DarkButton>
            </div>
            )
          : (
            <DarkButton onClick={() => { void handleTanCreate() }} disabled={!checkEmail(state.email ?? '')} fullwidth>
              {translate('tourist.continue')}
            </DarkButton>
            )
      }
      <Dialog open={isModalOpen}>
        <div className='p-6 flex flex-col justify-center items-center gap-4'>
          <Image draggable={false} alt='img' width={100} height={100} src={resolveImageUrl('/uploads/icons/payment-success.png')} className='w-16' />
          <h1 className='text-textHigh font-bold text-xl text-center'>{translate('tourist.success')}</h1>
          <p className='text-textMid text-sm font-medium text-center'>{translate('tourist.success.desc')}</p>
          <MCButton fullwidth onClick={() => { void onClose() }} size='sm'>Complete</MCButton>
        </div>
      </Dialog>
    </div>
  )
}
export default EmailInput
